import React, { useState, useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { useNavigate } from 'react-router';
import { useStylesHook } from 'src/hooks/utilHooks';
import { Box, Stepper, Step, StepLabel, Stack, Typography, Button, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { formatMessage } from 'src/i18n/intl';
import Locale from 'src/i18n';
import { generateLabelIcon, getActiveStep, stepModes } from 'src/asio-neon-components/Stepper/utils';
import getStyles from 'src/asio-neon-components/Stepper/styles';
import SuccessScreen from 'src/asio-neon-components/SuccessScreen';

const INITIAL_STEP = 0;

const StyledStepper = styled(Stepper)(() => ({
    '& .MuiStepConnector-root': {
        flexGrow: 2,
        alignItems: 'center',
        marginTop: '8px',
    },
    '& .MuiStep-root': {
        justifyContent: 'center',
    },
}));

const MuiStepper = props => {
    const {
        steps,
        errors = [],
        globalActions = [],
        successScreenProps,
        customBack,
        customNext,
        rootTab = '/usersNew',
    } = props;
    const navigate = useNavigate();
    const [activeStepIndex, setActiveStepIndex] = useState(0);

    const numberOfSteps = steps?.length || 0;
    const isSuccessScreen = !isEmpty(successScreenProps);

    const styles = useStylesHook(getStyles);

    const handleBack = useCallback(() => {
        setActiveStepIndex(prevActiveStep => prevActiveStep - 1);
    }, []);

    const activeStep = getActiveStep(activeStepIndex, numberOfSteps, isSuccessScreen, customNext);

    const handleProceed = shouldClose => {
        let proceed = true;
        if (steps[activeStepIndex].onNext) {
            proceed = steps[activeStepIndex].onNext(() => setActiveStepIndex(prevActiveStep => prevActiveStep + 1));
        }

        if (proceed) {
            setActiveStepIndex(prevActiveStep => prevActiveStep + 1);
        }

        if (shouldClose) {
            navigate(rootTab);
        }
    };

    const handleNextClick = () => {
        if (activeStep.mode === stepModes.CLOSE) {
            navigate(rootTab);
        } else {
            handleProceed(activeStep.mode === stepModes.SUBMIT_AND_CLOSE);
        }
    };

    const proceedDisabled =
        // check for custom step disabling
        (activeStep.mode !== stepModes.CLOSE && steps[activeStepIndex]?.proceedDisabled) ||
        // default check for errors
        (activeStep.mode === stepModes.NEXT && get(errors, [activeStepIndex], false));

    return (
        <Stack sx={styles.stepperContainer}>
            {steps.length !== 1 && (
                <Paper sx={{ px: '16px', pb: '8px' }}>
                    <StyledStepper
                        sx={{ display: 'inline-flex', width: '100%' }}
                        activeStep={activeStepIndex}
                        variant="progress">
                        {steps?.map((item, index) => (
                            <Step
                                sx={{ flex: 1, width: 'fit-content' }}
                                key={item.title}
                                completed={index > activeStepIndex}>
                                <StepLabel sx={{ width: 'fit-content !important' }}>
                                    <Box
                                        // layout gets disturbed when there are 2 steps total
                                        // className={index > activeStepIndex ? 'inactive-step' : 'active-step'}
                                        sx={{ display: 'flex', alignItems: 'center' }}
                                        width="fit-content">
                                        <Box className="stepper-icon">
                                            {generateLabelIcon(index, activeStepIndex, get(errors, [index], false))}
                                        </Box>
                                        <Box width="fit-content">
                                            <Stack direction="row" alignItems="center" justifyContent="center">
                                                <Typography variant="body1" width="fit-content">
                                                    {item.title}
                                                </Typography>
                                            </Stack>
                                            {item.label && (
                                                <Typography className="caption-text" variant="caption">
                                                    {item.label}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </StepLabel>
                            </Step>
                        ))}
                    </StyledStepper>
                </Paper>
            )}
            <Stack flexGrow={1}>
                <Stack sx={styles.contentContainer} flexGrow={1} justifyContent="center" alignItems="center">
                    {activeStep.mode === stepModes.CLOSE && isSuccessScreen ? (
                        <Box sx={styles.successScreenContainer}>
                            <SuccessScreen {...successScreenProps} />
                        </Box>
                    ) : (
                        steps[activeStepIndex].content
                    )}
                </Stack>
                <Paper sx={{ p: 2 }}>
                    <Stack direction="row-reverse" justifyContent="space-between" spacing={1}>
                        <Stack direction="row" spacing={1}>
                            {activeStep.mode === stepModes.NEXT && steps[activeStepIndex].customActions}
                            {!customBack?.hide && (
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    size="medium"
                                    disabled={customBack?.disabled || activeStepIndex === INITIAL_STEP}
                                    onClick={handleBack}>
                                    {customBack?.label || formatMessage(Locale.back)}
                                </Button>
                            )}
                            <Button
                                size="medium"
                                variant="contained"
                                color="secondary"
                                onClick={handleNextClick}
                                disabled={proceedDisabled}>
                                {activeStep.label}
                            </Button>
                        </Stack>
                        {activeStep.mode !== 'close' && <Stack spacing={1}>{globalActions}</Stack>}
                    </Stack>
                </Paper>
            </Stack>
        </Stack>
    );
};

MuiStepper.propTypes = {
    steps: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            label: PropTypes.string,
            content: PropTypes.node,
            customActions: PropTypes.arrayOf(PropTypes.node),
            proceedDisabled: PropTypes.bool,

            // should include 'onSuccess' argument, behavior of which is to move you to the next step
            onNext: PropTypes.func,
        })
    ).isRequired,

    // 'errors' array is used to control error state of the steps from outside
    // of the Stepper and provide proper indication
    //
    // use 'setErrorByIndex' from 'Stepper/utils' when you need to control errors state
    errors: PropTypes.arrayOf(PropTypes.bool),
    globalActions: PropTypes.arrayOf(PropTypes.node),
    successScreenProps: PropTypes.shape({
        title: PropTypes.string.isRequired,
        desc: PropTypes.string,
    }),
    // customize default actions
    customNext: PropTypes.shape({
        label: PropTypes.string,
        hide: PropTypes.bool,
        disabled: PropTypes.bool,
    }),
    customBack: PropTypes.shape({
        label: PropTypes.string,
        hide: PropTypes.bool,
        disabled: PropTypes.bool,
    }),
    // the route that you'd be taken to by clicking 'X' or 'Cancel'
    rootTab: PropTypes.string,
};

MuiStepper.defaultProps = {
    errors: [],
    globalActions: [],
    successScreenProps: null,
    customNext: null,
    customBack: null,
    rootTab: '/usersNew',
};

export default MuiStepper;
